import React, { useEffect, useState } from 'react';
import { Building2, Users, CreditCard, Activity } from 'lucide-react';
import { saasApi } from '../../utils/saasApi';

function Stat({ icon: Icon, label, value, tone = 'amber' }) {
  const toneCls = {
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
  }[tone];
  return (
    <div className="bg-white border rounded-xl p-5 flex items-start gap-4" data-testid={`sa-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`h-11 w-11 rounded-lg border flex items-center justify-center ${toneCls}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-[12px] uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="text-2xl font-bold text-zinc-900 mt-0.5">{value}</div>
      </div>
    </div>
  );
}

export default function SuperAdminOverview() {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tx, setTx] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [t, p, x] = await Promise.all([
          saasApi.listTenants(),
          saasApi.listPlans(),
          saasApi.listTransactions(),
        ]);
        setTenants(t || []);
        setPlans(p || []);
        setTx(x || []);
      } catch (_) {}
      setLoading(false);
    })();
  }, []);

  const activeTenants = tenants.filter((t) => t.status === 'active').length;
  const suspended = tenants.filter((t) => t.status === 'suspended').length;
  const paidTx = tx.filter((r) => r.payment_status === 'paid').length;
  const mrrEstimate = tenants.reduce((sum, t) => {
    const plan = plans.find((p) => p.code === t.plan_code);
    return sum + (plan ? plan.price_monthly : 0);
  }, 0);

  return (
    <div className="p-8 max-w-6xl">
      <h1
        className="text-2xl font-bold text-zinc-900"
        style={{ fontFamily: 'Manrope' }}
        data-testid="sa-overview-title"
      >
        Platform overview
      </h1>
      <p className="text-sm text-zinc-500 mt-1">
        High-level snapshot of your SaaS business.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Stat icon={Building2} label="Active Tenants" value={loading ? '…' : activeTenants} tone="amber" />
        <Stat icon={Users} label="Plans Configured" value={loading ? '…' : plans.length} tone="blue" />
        <Stat icon={CreditCard} label="Paid Transactions" value={loading ? '…' : paidTx} tone="emerald" />
        <Stat icon={Activity} label="Estimated MRR (USD)" value={loading ? '…' : `$${mrrEstimate.toFixed(0)}`} tone="violet" />
      </div>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-zinc-900">Tenants</h3>
            <span className="text-xs text-zinc-500">
              {activeTenants} active · {suspended} suspended
            </span>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {tenants.slice(0, 8).map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between p-3 border rounded-lg"
                data-testid={`sa-overview-tenant-${t.slug}`}
              >
                <div>
                  <div className="text-sm font-medium text-zinc-900">{t.name}</div>
                  <div className="text-[11px] text-zinc-500">
                    {t.slug} · {t.isolation_mode} · {t.plan_name || t.plan_code}
                  </div>
                </div>
                <span
                  className={`text-[11px] font-semibold px-2 py-1 rounded ${
                    t.status === 'suspended'
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  }`}
                >
                  {t.status}
                </span>
              </div>
            ))}
            {!tenants.length && (
              <p className="text-sm text-zinc-500">No tenants yet. Create one to begin.</p>
            )}
          </div>
        </div>
        <div className="bg-white border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-zinc-900">Recent transactions</h3>
            <span className="text-xs text-zinc-500">{tx.length} total</span>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {tx.slice(0, 8).map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between p-3 border rounded-lg"
                data-testid={`sa-overview-tx-${r.session_id}`}
              >
                <div>
                  <div className="text-sm font-medium text-zinc-900">
                    {r.tenant_slug} — {r.plan_code}
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    ${(r.amount || 0).toFixed(2)} {r.currency?.toUpperCase()}
                  </div>
                </div>
                <span
                  className={`text-[11px] font-semibold px-2 py-1 rounded ${
                    r.payment_status === 'paid'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-amber-50 text-amber-700 border border-amber-200'
                  }`}
                >
                  {r.payment_status}
                </span>
              </div>
            ))}
            {!tx.length && (
              <p className="text-sm text-zinc-500">No payments yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
